import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Copy } from 'lucide-react';
import { t } from '../../lib/i18n';

/**
 * Shows a secret exactly once (API key, webhook signing secret). It cannot be closed until the person ticks that
 * they have stored it; Escape and clicking outside do nothing. Mount it only while there is a secret to show, and
 * drop the secret from state in `onClose`.
 */
export function SecretDialog({
  title,
  description,
  secret,
  secretLabel,
  onClose,
}: {
  title: string;
  description?: string;
  secret: string;
  secretLabel: string;
  onClose: () => void;
}) {
  const id = useId();
  const [ack, setAck] = useState(false);
  const [copied, setCopied] = useState<'' | 'ok' | 'fail'>('');
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    field.current?.focus();
    field.current?.select();
    return () => previous?.focus?.();
  }, []);

  const trap = (e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !box.current) return;
    const items = Array.from(box.current.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)'));
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied('ok');
    } catch {
      setCopied('fail');
      field.current?.select();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-desc`}
        onKeyDown={trap}
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
      >
        <h2 id={`${id}-title`} className="text-lg font-semibold text-slate-900">
          {title}
        </h2>
        <p id={`${id}-desc`} className="mt-1 text-sm text-slate-700">
          {description ?? t('p2w.secret.once')}
        </p>
        <label htmlFor={`${id}-secret`} className="mt-4 block text-sm font-medium text-slate-800">
          {secretLabel}
        </label>
        <div className="mt-1 flex gap-2">
          <input
            ref={field}
            id={`${id}-secret`}
            readOnly
            value={secret}
            spellCheck={false}
            autoComplete="off"
            className="block min-h-10 w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-1.5 font-mono text-sm text-slate-900"
          />
          <button
            type="button"
            onClick={copy}
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <Copy aria-hidden="true" className="h-4 w-4" />
            {t('p2w.secret.copy')}
          </button>
        </div>
        <p role="status" className="mt-1 min-h-5 text-xs text-slate-600">
          {copied === 'ok' ? t('p2w.secret.copied') : copied === 'fail' ? t('p2w.secret.copyFailed') : ''}
        </p>
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-800">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4" />
          <span>{t('p2w.secret.confirm')}</span>
        </label>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            disabled={!ack}
            onClick={onClose}
            className="inline-flex min-h-10 items-center rounded-md bg-brand px-4 text-sm font-medium text-on-brand hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('p2w.secret.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
